/**
 * Syncs a custom scrollbar thumb with a nested slideshow-slides scroller.
 * Used by bonbons, parties-events, pastry-inspired, and collection carousels.
 */
class CarouselScrollIndicator extends HTMLElement {
  /** @type {number[]} */
  static #retryDelays = [0, 50, 150, 400, 1000];

  connectedCallback() {
    this.#thumb = this.querySelector('[ref="thumb"]');
    this.#indicator = this.#thumb?.parentElement ?? this.querySelector('[class*="__indicator"]');

    if (this.hasAttribute('data-sync-arrows') || this.classList.contains('bonbons-carousel__wrapper')) {
      this.#arrows = this.querySelector('slideshow-arrows');
    }

    this.#bindTrack();
    if (!this.#track) return;

    this.#track.addEventListener('scroll', this.#update, { passive: true });
    this.#track.addEventListener('load', this.#update, { passive: true, capture: true });

    this.#resizeObserver = new ResizeObserver(this.#scheduleUpdate);
    this.#resizeObserver.observe(this.#track);

    this.#slideshow = this.querySelector('slideshow-component');
    if (this.#slideshow) {
      this.#resizeObserver.observe(this.#slideshow);

      this.#mutationObserver = new MutationObserver(this.#scheduleUpdate);
      this.#mutationObserver.observe(this.#slideshow, {
        attributes: true,
        attributeFilter: ['in-viewport'],
      });
    }

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.#scheduleRetries();
        }
      },
      { rootMargin: '100px' },
    );
    this.#intersectionObserver.observe(this);

    customElements.whenDefined('slideshow-component').then(() => {
      this.#bindTrack();
      this.#scheduleRetries();
    });

    this.#scheduleRetries();
  }

  disconnectedCallback() {
    this.#track?.removeEventListener('scroll', this.#update);
    this.#track?.removeEventListener('load', this.#update, { capture: true });
    this.#resizeObserver?.disconnect();
    this.#mutationObserver?.disconnect();
    this.#intersectionObserver?.disconnect();
    this.#retryTimeouts.forEach((id) => clearTimeout(id));
    this.#retryTimeouts = [];
    if (this.#rafId) cancelAnimationFrame(this.#rafId);
  }

  /** @type {HTMLElement | null | undefined} */
  #track;
  /** @type {HTMLElement | null | undefined} */
  #thumb;
  /** @type {HTMLElement | null | undefined} */
  #indicator;
  /** @type {HTMLElement | null | undefined} */
  #arrows;
  /** @type {HTMLElement | null | undefined} */
  #slideshow;
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;
  /** @type {MutationObserver | undefined} */
  #mutationObserver;
  /** @type {IntersectionObserver | undefined} */
  #intersectionObserver;
  /** @type {number[]} */
  #retryTimeouts = [];
  /** @type {number | undefined} */
  #rafId;

  #bindTrack() {
    const track = this.querySelector('slideshow-slides');
    if (track instanceof HTMLElement) {
      this.#track = track;
    }
  }

  #scheduleUpdate = () => {
    if (this.#rafId) cancelAnimationFrame(this.#rafId);
    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = undefined;
      this.#update();
    });
  };

  #scheduleRetries() {
    this.#retryTimeouts.forEach((id) => clearTimeout(id));
    this.#retryTimeouts = [];

    for (const delay of CarouselScrollIndicator.#retryDelays) {
      const id = window.setTimeout(() => this.#scheduleUpdate(), delay);
      this.#retryTimeouts.push(id);
    }
  }

  #update = () => {
    if (!this.#track?.isConnected) {
      this.#bindTrack();
    }

    const track = this.#track;
    if (!track) return;

    const { scrollLeft, scrollWidth, clientWidth } = track;

    if (clientWidth === 0) return;

    const maxScroll = scrollWidth - clientWidth;
    const isScrollable = maxScroll > 1;

    this.#indicator?.toggleAttribute('hidden', !isScrollable);
    this.#arrows?.toggleAttribute('hidden', !isScrollable);

    if (!this.#thumb) return;

    if (!isScrollable) {
      this.#thumb.style.width = '100%';
      this.#thumb.style.left = '0';
      return;
    }

    const thumbRatio = clientWidth / scrollWidth;
    const thumbWidth = Math.max(thumbRatio * 100, 8);
    const thumbLeft = (scrollLeft / maxScroll) * (100 - thumbWidth);

    this.#thumb.style.width = `${thumbWidth}%`;
    this.#thumb.style.left = `${thumbLeft}%`;
  };
}

if (!customElements.get('carousel-scroll-indicator')) {
  customElements.define('carousel-scroll-indicator', CarouselScrollIndicator);
}
